import paper from 'paper';
import type { INode } from 'svgson';
import { PathConverter } from './pathConverter';
import { PaperPathUtils } from './util.paper';
import { ClipperUtils } from './util.clipper';
import { CUTLINER_OPTS } from '../../S/Constant/stickerizer.constant';
import type { VectorErrorCode } from '../../S/Constant/error-code';
import { VECTOR_ERROR_CODE } from '../../S/Constant/error-code';
import type {
  ApplyOffsetFromSvgErrorResponse,
  ApplyOffsetFromSvgOptions,
  ApplyOffsetResult,
} from '../../S/api.dto';

const MILLIMETERS_PER_INCH = 25.4;

export class ApplyOffsetError extends Error {
  public readonly code: VectorErrorCode;
  public readonly detail?: unknown;

  constructor(code: VectorErrorCode, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApplyOffsetError';
    this.code = code;
    this.detail = detail;
    Object.setPrototypeOf(this, ApplyOffsetError.prototype);
  }
}

interface ApplyOffsetParams {
  svgString: string;
  offsetMm: number;
  accuracy?: number;
  isRetainOriginalSvg?: boolean;
  smoothStrength?: number;
}

/**
 * SVG 문자열에 오프셋을 적용해 새로운 SVG 문자열을 반환한다.
 */
export async function applyOffsetFromSvg({
  svgString,
  offsetMm,
  accuracy = 10,
  isRetainOriginalSvg = false,
  smoothStrength = 5,
}: ApplyOffsetParams): Promise<ApplyOffsetResult> {
  const options: ApplyOffsetFromSvgOptions = {
    offsetMm,
    accuracy,
    isRetainOriginalSvg,
    smoothStrength,
  };

  try {
    validateParams(svgString, offsetMm, accuracy, smoothStrength);

    const sanitizedSvg = removeImageElements(svgString);
    const svgJson = await parseSvgString(sanitizedSvg);

    requireSinglePathNode(svgJson);

    PaperPathUtils.ensureProjectInitialized();

    const paperPaths = convertSvgJsonToPaperPaths(svgJson);

    const originalPath = requireSinglePaperPath(paperPaths);
    const offsetPx = convertMillimeterToPixel(offsetMm, CUTLINER_OPTS.DPI_300);
    const smoothStrengthPx =
      smoothStrength > 0 ? convertMillimeterToPixel(smoothStrength, CUTLINER_OPTS.DPI_300) : 0;
    const offsetPath = buildOffsetCompoundPath(originalPath, offsetPx, accuracy, smoothStrengthPx);
    const compoundPath = createResultCompoundPath(offsetPath, originalPath, isRetainOriginalSvg);
    const filteredCompound = filterSmallAreas(compoundPath);
    const meta = extractSvgMeta(svgJson, offsetPx, isRetainOriginalSvg);
    const svgResult = PathConverter.paperPathToSvgString(
      filteredCompound,
      CUTLINER_OPTS.DEFAULT_STROKE_STYLE,
      meta,
    );

    return {
      success: true,
      svg: svgResult,
      meta,
      options,
    };
  } catch (error) {
    return toErrorResult(error, options);
  }
}

/**
 * 파라미터 유효성을 검증한다.
 * @param svg SVG 문자열
 * @param offsetMm 밀리미터 단위 오프셋 값
 * @param accuracy 클리퍼 스케일 값
 * @param smoothStrength smoothing 강도 (mm)
 */
function validateParams(svg: string, offsetMm: number, accuracy: number, smoothStrength: number): void {
  if (typeof svg !== 'string' || svg.trim() === '') {
    // SVG 문자열 유효성 검증 실패
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.INVALID_INPUT, 'SVG 문자열이 비어 있습니다.');
  }
  if (!Number.isFinite(offsetMm)) {
    // offset_mm 유효성 검증 실패
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.INVALID_INPUT, 'offset_mm 값이 올바르지 않습니다.');
  }
  if (!Number.isFinite(accuracy) || accuracy <= 0) {
    // accuracy 유효성 검증 실패
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.INVALID_INPUT, 'accuracy 값은 0보다 큰 숫자여야 합니다.');
  }
  if (!Number.isFinite(smoothStrength) || smoothStrength < 0) {
    // smoothStrength 유효성 검증 실패
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.INVALID_INPUT, 'smoothStrength 값은 0 이상이어야 합니다.');
  }
}

/**
 * SVG 문자열에서 <image> 요소를 제거한다.
 * @param svg SVG 문자열
 * @returns image 요소가 제거된 SVG 문자열
 */
function removeImageElements(svg: string): string {
  return svg.replace(/<image[\s\S]*?\/?>/gi, '');
}

/**
 * SVG에 포함된 path 요소가 하나인지 확인한다.
 * @param svgJson 파싱된 SVG 노드
 * @returns 단일 path 노드
 */
function requireSinglePathNode(svgJson?: INode): INode {
  if (!svgJson) {
    // svgson 파싱 자체가 실패한 경우
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.SVG_PARSE_FAILED, 'SVG 파싱 결과가 올바르지 않습니다.');
  }
  const paths = collectPathNodes(svgJson);
  if (paths.length === 0) {
    // path 요소가 전혀 없는 경우
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.SVG_PATH_NOT_FOUND, 'SVG에 path 요소가 존재하지 않습니다.');
  }
  if (paths.length > 1) {
    // path 요소가 둘 이상인 경우
    throw new ApplyOffsetError(
      VECTOR_ERROR_CODE.SVG_MULTIPLE_PATHS,
      'SVG에는 하나의 path 요소만 포함되어야 합니다.',
    );
  }
  const pathNode = paths[0];
  if (!pathNode) {
    // path 노드를 찾았으나 내부적으로 비정상인 경우
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.SVG_PATH_NOT_FOUND, 'path 요소를 찾을 수 없습니다.');
  }
  if (!pathNode.attributes?.d || pathNode.attributes.d.trim() === '') {
    // d 속성이 누락된 경우
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.SVG_PATH_MISSING_D, 'path 요소에 d 속성이 없습니다.');
  }
  return pathNode;
}

/**
 * path 요소 목록을 수집한다.
 * @param root SVG 루트 노드
 * @returns path 노드 배열
 */
function collectPathNodes(root?: INode): INode[] {
  if (!root) return [];
  const stack: INode[] = [root];
  const paths: INode[] = [];
  while (stack.length) {
    const current = stack.pop();
    if (!current) continue;
    if (current.name === 'path') paths.push(current);
    if (Array.isArray(current.children)) stack.push(...current.children);
  }
  return paths;
}

/**
 * Paper Path 배열이 하나인지 검증한다.
 * @param paths Paper.js Path 배열
 */
function requireSinglePaperPath(paths?: paper.Path[]): paper.Path {
  if (!paths || paths.length === 0) {
    // Paper Path 변환 결과가 비어 있는 경우
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.PAPER_PATH_EMPTY, 'Paper Path 변환 결과가 비어 있습니다.');
  }
  if (paths.length > 1) {
    // Paper Path 변환 결과가 여러 개인 경우
    throw new ApplyOffsetError(
      VECTOR_ERROR_CODE.PAPER_PATH_MULTIPLE,
      'SVG가 단일 Path 형태가 아니므로 처리할 수 없습니다.',
    );
  }
  const [path] = paths;
  if (!path) {
    // 변환 결과가 비정상인 경우
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.PAPER_PATH_INVALID, 'Paper Path 결과가 올바르지 않습니다.');
  }
  return path;
}

/**
 * 밀리미터 값을 픽셀 단위로 변환한다.
 * @param offsetMm 밀리미터 값
 * @param dpi DPI 값
 * @returns 픽셀 값
 */
function convertMillimeterToPixel(offsetMm: number, dpi: number): number {
  return (offsetMm * dpi) / MILLIMETERS_PER_INCH;
}

/**
 * 오프셋이 적용된 CompoundPath를 생성한다.
 * @param path 원본 Path
 * @param offsetPx 픽셀 단위 오프셋
 * @param scale 클리퍼 스케일 값
 * @param smoothStrengthPx 픽셀 단위 smooth 강도
 * @returns 오프셋된 CompoundPath
 */
function buildOffsetCompoundPath(
  path: paper.Path,
  offsetPx: number,
  scale: number,
  smoothStrengthPx: number,
): paper.CompoundPath {
  try {
    const polygons = PathConverter.paperPathToClipperPolygons(path, scale);
    const united = ClipperUtils.unionPolygons(polygons);
    const offsetDistance = Math.round(offsetPx * scale);
    const smoothOffsetDistance = Math.round(smoothStrengthPx * scale);
    const offsetPolygons =
      offsetDistance === 0
        ? united
        : smoothOffsetDistance > 0
          ? ClipperUtils.smoothOffsetPolygons({
              polys: united,
              targetOffsetDistance: offsetDistance,
              smoothOffsetAdder: smoothOffsetDistance,
            })
          : ClipperUtils.offsetPolygons({ polys: united, offsetDistance });
    if (!offsetPolygons || offsetPolygons.length === 0) {
      // 오프셋 결과가 완전히 사라진 경우
      throw new ApplyOffsetError(VECTOR_ERROR_CODE.OFFSET_EMPTY_RESULT, '오프셋 결과가 비어 있습니다.');
    }
    const merged = ClipperUtils.unionPolygons(offsetPolygons);
    return PathConverter.clipperPolygonsToPaperPath(merged, scale);
  } catch (error) {
    if (error instanceof ApplyOffsetError) {
      throw error;
    }
    // 예상치 못한 오프셋 계산 오류
    throw new ApplyOffsetError(
      VECTOR_ERROR_CODE.OFFSET_PROCESS_FAILED,
      '오프셋 처리 중 오류가 발생했습니다.',
      { cause: getErrorMessage(error) },
    );
  }
}

/**
 * 결과 CompoundPath에 원본 Path를 포함할지 결정한다.
 * @param offset 오프셋 결과
 * @param original 원본 Path
 * @param shouldRetain 원본 포함 여부
 * @returns 최종 CompoundPath
 */
function createResultCompoundPath(
  offset: paper.CompoundPath,
  original: paper.Path,
  shouldRetain: boolean,
): paper.CompoundPath {
  if (!shouldRetain) return offset;
  const compound = new paper.CompoundPath({});
  compound.addChild(offset.clone());
  compound.addChild(original.clone());
  return compound;
}

/**
 * 작은 면적 경로를 제거한다.
 * @param path 입력 CompoundPath
 * @returns 면적 필터링이 적용된 CompoundPath
 */
function filterSmallAreas(path: paper.CompoundPath): paper.CompoundPath {
  const filtered = PaperPathUtils.filterSmallAreaAndHolePaths(path, CUTLINER_OPTS.MIN_MERGE_FILTER_AREA);
  if (!filtered) {
    return new paper.CompoundPath({});
  }
  if (filtered instanceof paper.Path) {
    const compound = new paper.CompoundPath({});
    compound.addChild(filtered.clone());
    return compound;
  }
  return filtered as paper.CompoundPath;
}

/**
 * SVG 메타 정보를 추출하고 오프셋에 맞게 보정한다.
 * @param svgJson SVG 루트 노드
 * @param offsetPx 픽셀 단위 오프셋
 * @returns 보정된 SVG 메타 정보
 */
function extractSvgMeta(
  svgJson: INode,
  offsetPx: number,
  shouldRetainOriginalSvg: boolean,
): { width: number; height: number; viewBox: string } {
  const parsedWidth = parseFloat(String(svgJson.attributes?.width || CUTLINER_OPTS.DEFAULT_SIZE));
  const parsedHeight = parseFloat(String(svgJson.attributes?.height || CUTLINER_OPTS.DEFAULT_SIZE));
  const width = Number.isFinite(parsedWidth) ? parsedWidth : CUTLINER_OPTS.DEFAULT_SIZE;
  const height = Number.isFinite(parsedHeight) ? parsedHeight : CUTLINER_OPTS.DEFAULT_SIZE;
  const viewBox = svgJson.attributes?.viewBox || `0 0 ${width} ${height}`;

  if (offsetPx === 0) {
    return { width, height, viewBox };
  }

  if (offsetPx < 0 && shouldRetainOriginalSvg) {
    return { width, height, viewBox };
  }

  const adjustedWidth = width + offsetPx * 2;
  const adjustedHeight = height + offsetPx * 2;
  const adjustedViewBox = `${-offsetPx} ${-offsetPx} ${adjustedWidth} ${adjustedHeight}`;
  return { width: adjustedWidth, height: adjustedHeight, viewBox: adjustedViewBox };
}

async function parseSvgString(svgString: string): Promise<INode> {
  try {
    return await PathConverter.svgStringToSvgJson(svgString);
  } catch (error) {
    // svgson 파싱 실패
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.SVG_PARSE_FAILED, 'SVG 문자열을 파싱하지 못했습니다.', {
      cause: getErrorMessage(error),
    });
  }
}

function convertSvgJsonToPaperPaths(svgJson: INode): paper.Path[] {
  try {
    return PathConverter.svgJsonToPaperPaths(
      svgJson,
      CUTLINER_OPTS.MIN_IGNORE_PATH_AREA,
      CUTLINER_OPTS.NORMALIZE_VIEWPORT,
    );
  } catch (error) {
    // svgJson → Paper Path 변환 실패
    throw new ApplyOffsetError(VECTOR_ERROR_CODE.PAPER_CONVERSION_FAILED, 'Paper Path 변환에 실패했습니다.', {
      cause: getErrorMessage(error),
    });
  }
}

function toErrorResult(error: unknown, options: ApplyOffsetFromSvgOptions): ApplyOffsetFromSvgErrorResponse {
  if (error instanceof ApplyOffsetError) {
    return {
      success: false,
      error: {
        // 입력 에러는 그대로 전달
        code: error.code,
        message: error.message,
        detail: error.detail,
      },
      options,
    };
  }

  return {
    success: false,
    error: {
      // 예상치 못한 기타 오류
      code: VECTOR_ERROR_CODE.OFFSET_PROCESS_FAILED,
      message: '오프셋 처리 중 알 수 없는 오류가 발생했습니다.',
      detail: { cause: getErrorMessage(error) },
    },
    options,
  };
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch (e) {
    return String(error);
  }
}
